// CodeMirror extension: detect http(s) URLs in the document and make
// them clickable. Used by the read-only CodeViewer (response bodies,
// flow step output, code export) — NOT by editors, where a click should
// place the cursor rather than navigate.
//
// A match is decorated with .cm-url-link (hover underline via
// styles.css) and the click handler opens the URL in a new tab with
// noopener. Only http/https schemes ever match, so a hostile response
// body can't smuggle javascript: or file: URLs into a click.

import { Decoration, EditorView, MatchDecorator, ViewPlugin } from "@codemirror/view";
import type { DecorationSet, ViewUpdate } from "@codemirror/view";
import type { Extension } from "@codemirror/state";

// Ends on a character that's plausibly part of the URL — trailing
// quotes, brackets, and sentence punctuation stay outside the link.
const URL_RE = /https?:\/\/[^\s"'<>\\]*[^\s"'<>\\.,;:!?)\]}]/g;

const urlDecorator = new MatchDecorator({
  regexp: URL_RE,
  decoration: (m) =>
    Decoration.mark({
      class: "cm-url-link",
      attributes: {
        "data-url": m[0],
        title: `Open ${m[0]} in a new tab`,
      },
    }),
});

const urlHighlighter = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = urlDecorator.createDeco(view);
    }
    update(update: ViewUpdate) {
      this.decorations = urlDecorator.updateDeco(update, this.decorations);
    }
  },
  { decorations: (v) => v.decorations },
);

const urlClicker = EditorView.domEventHandlers({
  click: (event) => {
    const target = (event.target as HTMLElement | null)?.closest?.(".cm-url-link");
    if (!target) return false;
    // Don't hijack an in-progress text selection drag.
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return false;
    const url = target.getAttribute("data-url");
    if (!url || !/^https?:\/\//i.test(url)) return false;
    window.open(url, "_blank", "noopener,noreferrer");
    event.preventDefault();
    return true;
  },
});

export function hyperlinkExtension(): Extension {
  return [urlHighlighter, urlClicker];
}
